// @givmo/api-client — request correlation.
//
// Ports the Flutter `RequestLoggingInterceptor` ID contract
// (givmo-webapp/lib/core/interceptors/request_logging_interceptor.dart). Every
// outbound request is stamped with an `X-Request-Id` so the backend can echo it
// into logs + the `{errors:[{request_id}]}` envelope, closing the
// user-quoted-ref → backend-log-line loop.
//
// Format: `<prefix>-<base36 ts>-<base36 rand>` — total length 13-17 chars, well
// inside the backend's `\A[a-zA-Z0-9_-]{8,64}\Z` validation regex (the
// cross-repo contract). `prefix` is per-app so backend logs and Sentry traces
// attribute to the originating frontend.

/**
 * Per-app `X-Request-Id` prefix. `wb`/`sa`/`dw` are fixed by the Combined Plan
 * (03 §4); `cw`/`pw`/`cn`/`db` are the scaffold-assigned values for the
 * remaining apps; `ch` is the charity portal (CPA-WEB1). See the repo README
 * "Per-app X-Request-Id prefixes" table.
 *
 * A Board director is the console's own signed-in account (CBG-SEC2, D-CBG-16), so the
 * director pages ride their host's one client and its prefix (`ch` in the charity console):
 * the capacity a call is made in is its ROUTE (`/board-director/organizations/…` versus the
 * officer's `/board/…`), not a second client. `bx`, the retired magic-link director client's
 * prefix, is gone with that client.
 * `bd` was the legacy standalone Board host's (CBG-SPA1, retired in CBG-MOD2-WEB); it
 * stays a member because `@givmo/board`'s own test fixtures still configure it, the
 * officer suites as well as the director ones (e.g. `roster-page.test.tsx`).
 *
 * `co` is the company org console (EP-SUITE3), `company.givmo.io` — the console-as-home
 * an employer admin lands on, distinct from the employer portal's `wb` because the two
 * are separate deployed origins calling the same `/admin/company/*` routes with the same
 * credential: without a distinct prefix a backend log cannot say which surface a company
 * admin was on when a wallet deposit or a join-request decision went out.
 *
 * `em` is the Connect widgets embed (CW-WEB1). It is the only app whose traffic is
 * UNAUTHENTICATED and driven by third-party page views rather than by a Givmo user,
 * so its prefix is what separates embed-impression volume from real user traffic in
 * the backend's own rate/abuse view of the public payload endpoint.
 */
export type RequestIdPrefix =
  | 'wb'
  | 'sa'
  | 'dw'
  | 'cw'
  | 'pw'
  | 'cn'
  | 'db'
  | 'ch'
  | 'bd'
  | 'em'
  | 'co';

/** The backend's accepted `X-Request-Id` shape: `\A[a-zA-Z0-9_-]{8,64}\Z`. */
export const REQUEST_ID_PATTERN = /^[a-zA-Z0-9_-]{8,64}$/;

/** The canonical correlation header. */
export const REQUEST_ID_HEADER = 'X-Request-Id';

/**
 * Generate a correlation ID. The optional `rng` seam exists only for
 * deterministic tests; production leaves it unset and the `crypto`-backed path
 * runs.
 *
 * 32 bits of randomness drops the same-millisecond collision probability to
 * ~1-in-4B, matching the Flutter original's `Random.secure().nextInt(0xFFFFFFFF)`.
 */
export function generateRequestId(
  prefix: RequestIdPrefix,
  rng: () => number = secureRandomUint32,
): string {
  const ts = Date.now().toString(36);
  const rand = (rng() >>> 0).toString(36);
  return `${prefix}-${ts}-${rand}`;
}

function secureRandomUint32(): number {
  // `crypto` is available in every target runtime (browser, Node ≥22 globalThis,
  // Cloud Run SSR, Workers). Fall back to Math.random only if a host strips it
  // — correlation IDs are non-secret, so a weaker source is acceptable over a
  // hard failure in the request hot path.
  const c = globalThis.crypto;
  if (c && typeof c.getRandomValues === 'function') {
    const buf = new Uint32Array(1);
    c.getRandomValues(buf);
    return buf[0] ?? 0;
  }
  return Math.floor(Math.random() * 0xffffffff);
}
